import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useTitle } from '@/lib/useTitle'

const API = import.meta.env.VITE_API_URL ?? 'http://localhost:3001'

const DEFAULT_SETTINGS: Record<string, string> = {
  company_name: 'Velink SAS',
  company_address: '',
  company_siret: '',
  company_email: 'contact@velink.fr',
  commission_rate: '10',
  cgv_last_update: new Date().toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' }),
}

function useSettings() {
  const [settings, setSettings] = useState<Record<string, string>>(DEFAULT_SETTINGS)

  useEffect(() => {
    fetch(`${API}/api/public/settings`)
      .then((res) => {
        if (!res.ok) throw new Error()
        return res.json()
      })
      .then((data) => setSettings((prev) => ({ ...prev, ...data })))
      .catch(() => {})
  }, [])

  return settings
}

function Section({ id, title, children }: { id: string; title: string; children: React.ReactNode }) {
  return (
    <section id={id} className="scroll-mt-24">
      <h2 className="text-lg font-semibold text-text-primary mb-4 pb-2 border-b border-white/[0.08]">
        {title}
      </h2>
      <div className="space-y-3 text-text-secondary text-sm leading-relaxed">
        {children}
      </div>
    </section>
  )
}

function renderCustomCgv(content: string) {
  const blocks = content.split(/\n\n+/)
  return blocks.map((block, i) => {
    const lines = block.trim().split('\n')
    const first = lines[0]
    if (first.startsWith('## ')) {
      const body = lines.slice(1).join('\n').trim()
      return (
        <section key={i} className="scroll-mt-24 space-y-3">
          <h2 className="text-lg font-semibold text-text-primary mb-4 pb-2 border-b border-white/[0.08]">{first.slice(3)}</h2>
          {body && <p className="text-text-secondary text-sm leading-relaxed whitespace-pre-line">{body}</p>}
        </section>
      )
    }
    if (first.startsWith('# ')) {
      return <h1 key={i} className="text-2xl font-bold text-text-primary">{first.slice(2)}</h1>
    }
    const isList = lines.every((l) => l.trim().startsWith('- ') || l.trim().startsWith('* '))
    if (isList) {
      return (
        <ul key={i} className="list-disc list-inside space-y-1 pl-2 text-text-secondary text-sm">
          {lines.map((l, j) => <li key={j}>{l.trim().slice(2)}</li>)}
        </ul>
      )
    }
    return <p key={i} className="text-text-secondary text-sm leading-relaxed whitespace-pre-line">{block.trim()}</p>
  })
}

function P({ children }: { children: React.ReactNode }) {
  return <p>{children}</p>
}

function Ul({ items }: { items: string[] }) {
  return (
    <ul className="list-disc list-inside space-y-1 pl-2">
      {items.map((item, i) => <li key={i}>{item}</li>)}
    </ul>
  )
}

const TOC = [
  { id: 'objet',           label: '1. Objet et champ d\'application' },
  { id: 'definitions',     label: '2. Définitions' },
  { id: 'inscription',     label: '3. Inscription et comptes' },
  { id: 'services',        label: '4. Services proposés' },
  { id: 'missions',        label: '5. Création et gestion des missions' },
  { id: 'financier',       label: '6. Conditions financières' },
  { id: 'paiements',       label: '7. Paiements et séquestre' },
  { id: 'obligations-ent', label: '8. Obligations des entreprises' },
  { id: 'obligations-pre', label: '9. Obligations des prestataires' },
  { id: 'responsabilite',  label: '10. Responsabilité' },
  { id: 'resiliation',     label: '11. Résiliation et suspension' },
  { id: 'donnees',         label: '12. Données personnelles' },
  { id: 'pi',              label: '13. Propriété intellectuelle' },
  { id: 'modifications',   label: '14. Modifications des CGV' },
  { id: 'droit',           label: '15. Droit applicable et litiges' },
]

export default function CGVPage() {
  useTitle('Conditions Générales de Vente')
  const s = useSettings()

  const COMPANY_NAME    = s.company_name    || 'Velink SAS'
  const COMPANY_ADDRESS = s.company_address || '[Adresse du siège social]'
  const COMPANY_SIRET   = s.company_siret   || '[Numéro SIRET]'
  const COMPANY_EMAIL   = s.company_email   || 'contact@velink.fr'
  const COMMISSION_RATE = s.commission_rate || '10'
  const LAST_UPDATE     = s.cgv_last_update || new Date().toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })

  return (
    <div className="min-h-screen">
      {/* Navbar minimale */}
      <header className="glass-navbar fixed top-0 inset-x-0 z-50">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-accent/20 border border-accent/30 flex items-center justify-center">
              <span className="text-accent font-bold text-sm">V</span>
            </div>
            <span className="font-semibold text-text-primary text-lg tracking-tight">Velink</span>
          </Link>
          <Link to="/" className="glass-btn px-4 py-2 text-sm">
            ← Retour à l'accueil
          </Link>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 pt-28 pb-20">
        {/* En-tête */}
        <div className="mb-10">
          <h1 className="text-3xl font-bold text-text-primary mb-2">
            Conditions Générales de Vente
          </h1>
          <p className="text-text-muted text-sm">
            Dernière mise à jour : {LAST_UPDATE}
          </p>
          <div className="mt-4 px-4 py-3 rounded-xl bg-accent/10 border border-accent/20 text-[#8B9AFF] text-sm">
            Ces conditions régissent l'utilisation de la plateforme Velink. En créant un compte, vous acceptez l'intégralité des présentes CGV.
          </div>
        </div>

        <div className="flex gap-10 items-start">
          {/* Sommaire sticky — masqué si contenu custom */}
          {!s.cgv_content && (
            <nav className="hidden lg:block w-64 flex-shrink-0 sticky top-24 glass-card p-5 rounded-2xl">
              <p className="text-xs font-semibold text-text-muted uppercase tracking-wider mb-4">Sommaire</p>
              <ul className="space-y-2">
                {TOC.map((item) => (
                  <li key={item.id}>
                    <a
                      href={`#${item.id}`}
                      className="text-xs text-text-secondary hover:text-text-primary transition-colors block leading-snug"
                    >
                      {item.label}
                    </a>
                  </li>
                ))}
              </ul>
            </nav>
          )}

          {/* Contenu */}
          <div className="flex-1 space-y-10">

            {s.cgv_content ? (
              renderCustomCgv(s.cgv_content)
            ) : (<>

            <Section id="objet" title="1. Objet et champ d'application">
              <P>
                Les présentes Conditions Générales de Vente et d'Utilisation (ci-après « CGV ») ont pour objet de définir les droits et obligations de {COMPANY_NAME} et des utilisateurs de la plateforme Velink, accessible à l'adresse <strong className="text-text-primary">velink.fr</strong>.
              </P>
              <P>
                {COMPANY_NAME} exploite une plateforme numérique de mise en relation entre des <strong className="text-text-primary">entreprises clientes</strong> (donneurs d'ordre) et des <strong className="text-text-primary">prestataires indépendants</strong> pour la réalisation de prestations dans les domaines de l'événementiel et de la formation professionnelle.
              </P>
              <P>
                Velink agit en qualité d'intermédiaire. Elle n'est ni employeur ni sous-traitant des prestataires, et n'est pas partie aux contrats de prestation conclus entre les entreprises et les prestataires.
              </P>
              <P>
                Ces CGV s'appliquent à toute utilisation de la plateforme, qu'il s'agisse de la version web ou de l'application mobile. Toute utilisation implique l'acceptation pleine et entière des présentes CGV.
              </P>
            </Section>

            <Section id="definitions" title="2. Définitions">
              <Ul items={[
                '« Plateforme » : le site web velink.fr et l\'application mobile Velink.',
                '« Entreprise » : toute personne morale inscrite sur la plateforme en qualité de donneur d\'ordre pour publier des missions.',
                '« Prestataire » : toute personne physique ou morale inscrite sur la plateforme proposant ses services en qualité d\'indépendant.',
                '« Mission » : offre de prestation publiée par une entreprise (événement, formation, autre).',
                '« Assignment » : acceptation d\'une mission par un prestataire.',
                '« Séquestre » : mécanisme de rétention temporaire des fonds par la plateforme jusqu\'à confirmation de la fin de la mission.',
                `« Commission Velink » : frais de ${COMMISSION_RATE} % prélevés par Velink sur chaque transaction en contrepartie des services d'intermédiation.`,
                '« Stripe Connect / PayPal » : prestataires de services de paiement tiers utilisés par la plateforme.',
              ]} />
            </Section>

            <Section id="inscription" title="3. Inscription et comptes utilisateurs">
              <P>
                L'accès aux fonctionnalités de la plateforme nécessite la création d'un compte. L'inscription est gratuite et ouverte à toute personne physique majeure (18 ans ou plus) ou personne morale régulièrement immatriculée.
              </P>
              <P>
                <strong className="text-text-primary">Entreprises :</strong> l'inscription requiert une adresse email professionnelle, un nom de société et, pour accéder aux fonctionnalités avancées, un numéro SIRET valide. Les entreprises doivent être en capacité juridique de contracter.
              </P>
              <P>
                <strong className="text-text-primary">Prestataires :</strong> l'inscription requiert l'identité civile (nom, prénom), une adresse email et un numéro de téléphone. Le prestataire garantit exercer son activité de manière légale, notamment en étant enregistré comme auto-entrepreneur, freelance ou en société.
              </P>
              <P>
                L'utilisateur s'engage à fournir des informations exactes, complètes et à les maintenir à jour. Velink se réserve le droit de vérifier l'identité ou les qualifications des utilisateurs et de suspendre tout compte dont les informations s'avèrent fausses ou trompeuses.
              </P>
              <P>
                Les identifiants de connexion sont personnels et confidentiels. L'utilisateur est responsable de toute utilisation faite depuis son compte. Toute utilisation frauduleuse doit être signalée sans délai à {COMPANY_EMAIL}.
              </P>
            </Section>

            <Section id="services" title="4. Services proposés">
              <P>Velink propose les services suivants :</P>
              <Ul items={[
                'Publication et gestion de missions par les entreprises.',
                'Matching géolocalisé entre missions et prestataires disponibles à proximité.',
                'Notification push en temps réel des prestataires correspondant au profil recherché.',
                'Acceptation ou déclin de missions par les prestataires.',
                'Traitement sécurisé des paiements via Stripe Connect et PayPal (séquestre).',
                'Module de gestion de centre de formation (sessions, stagiaires, attestations, suivi Qualiopi).',
                'Système d\'évaluation des prestataires (ponctualité, comportement, retour client).',
                'Tableau de bord analytique pour les entreprises.',
                'Gestion documentaire (Kbis, RC Pro, RIB, contrats).',
              ]} />
              <P>
                Ces services peuvent évoluer. Velink se réserve le droit de modifier, suspendre ou interrompre tout ou partie des services, avec un préavis raisonnable sauf urgence ou cas de force majeure.
              </P>
            </Section>

            <Section id="missions" title="5. Création et gestion des missions">
              <P>
                <strong className="text-text-primary">Publication :</strong> l'entreprise publie une mission en précisant le type de prestation (événementiel, formation, autre), la date, le lieu, la durée, le budget proposé et une description. La mission est visible par les prestataires correspondant aux critères géographiques et de compétences.
              </P>
              <P>
                <strong className="text-text-primary">Acceptation :</strong> le prestataire dispose du libre choix d'accepter ou de décliner une mission. L'acceptation est ferme et engage le prestataire à réaliser la prestation dans les conditions décrites.
              </P>
              <P>
                <strong className="text-text-primary">Modification :</strong> l'entreprise peut modifier une mission publiée tant que son statut est « Ouverte » ou « Assignée ». Toute modification d'une mission déjà acceptée remet le prestataire en attente de re-confirmation et doit être justifiée.
              </P>
              <P>
                <strong className="text-text-primary">Annulation :</strong> l'entreprise peut annuler une mission ouverte. En cas d'annulation d'une mission assignée, les conditions d'annulation et de remboursement décrites à l'article 7 s'appliquent. Toute annulation abusive est susceptible de donner lieu à une suspension du compte.
              </P>
              <P>
                <strong className="text-text-primary">Clôture :</strong> à l'issue de la prestation, l'entreprise clôture la mission via la plateforme. Cette action déclenche la libération des fonds au prestataire et l'accès au formulaire d'évaluation.
              </P>
            </Section>

            <Section id="financier" title="6. Conditions financières">
              <P>
                <strong className="text-text-primary">Commission Velink :</strong> en contrepartie des services d'intermédiation, Velink prélève une commission de <strong className="text-text-primary">{COMMISSION_RATE} %</strong> sur le montant total de chaque transaction entre une entreprise et un prestataire. Cette commission est déduite automatiquement lors du paiement.
              </P>
              <P>
                <strong className="text-text-primary">Exemple :</strong> pour une mission facturée 180 €, Velink prélève {Math.round(180 * Number(COMMISSION_RATE) / 100)} € de commission. Le prestataire reçoit {180 - Math.round(180 * Number(COMMISSION_RATE) / 100)} €.
              </P>
              <P>
                <strong className="text-text-primary">TVA :</strong> les montants affichés sur la plateforme sont indiqués hors taxes (HT). La TVA applicable est calculée selon la réglementation en vigueur et dépend du statut fiscal de chaque partie. Chaque partie est responsable de ses obligations fiscales propres.
              </P>
              <P>
                <strong className="text-text-primary">Facturation :</strong> chaque prestataire est responsable de l'émission de sa propre facture à l'entreprise cliente pour les prestations réalisées. Velink n'agit pas comme mandataire de facturation sauf accord spécifique.
              </P>
              <P>
                Les tarifs de commission peuvent évoluer. Toute modification sera communiquée aux utilisateurs avec un préavis d'au moins 30 jours.
              </P>
            </Section>

            <Section id="paiements" title="7. Paiements et séquestre">
              <P>
                Velink utilise un mécanisme de <strong className="text-text-primary">séquestre</strong> : les fonds versés par l'entreprise sont retenus sur la plateforme jusqu'à confirmation de la fin de la mission, puis libérés au prestataire.
              </P>
              <P>
                <strong className="text-text-primary">Moyens de paiement acceptés :</strong>
              </P>
              <Ul items={[
                'Carte bancaire via Stripe Connect (Visa, Mastercard, American Express).',
                'Compte PayPal via PayPal Orders API.',
              ]} />
              <P>
                <strong className="text-text-primary">Stripe Connect :</strong> le prestataire doit créer et activer un compte Stripe Connect Express pour recevoir ses paiements. L'activation nécessite de fournir des informations d'identité à Stripe conformément à ses exigences KYC. Velink ne stocke pas de données bancaires.
              </P>
              <P>
                <strong className="text-text-primary">PayPal :</strong> le prestataire peut renseigner son adresse email PayPal pour recevoir des paiements via PayPal Payouts.
              </P>
              <P>
                <strong className="text-text-primary">Délais de versement :</strong> les fonds sont libérés au prestataire dans un délai de 1 à 5 jours ouvrés après la clôture de la mission, selon le prestataire de paiement utilisé.
              </P>
              <P>
                <strong className="text-text-primary">Litiges de paiement :</strong> tout litige relatif à un paiement doit être signalé à Velink dans les 7 jours suivant la clôture de la mission. Passé ce délai, le paiement est réputé accepté.
              </P>
              <P>
                <strong className="text-text-primary">Remboursements :</strong> en cas d'annulation d'une mission assignée avant le début de la prestation, l'entreprise est remboursée du montant versé, déduction faite des éventuels frais de traitement bancaire non récupérables. Aucun remboursement n'est possible une fois la mission clôturée et le paiement libéré.
              </P>
            </Section>

            <Section id="obligations-ent" title="8. Obligations des entreprises">
              <P>L'entreprise s'engage à :</P>
              <Ul items={[
                'Publier des missions réelles, légales et conformes à la réglementation applicable.',
                'Décrire avec précision et sincérité les missions publiées (nature, durée, lieu, rémunération).',
                'Honorer les engagements pris envers le prestataire une fois la mission acceptée.',
                'Ne pas tenter de contourner la plateforme pour traiter directement avec un prestataire trouvé via Velink (sous peine de suspension définitive du compte).',
                'S\'assurer que les prestations commandées sont conformes aux lois et réglementations en vigueur, notamment en matière de droit du travail et de formation professionnelle.',
                'Payer le montant convenu dans les délais impartis.',
                'Évaluer honnêtement les prestataires à l\'issue des missions.',
                'Conserver les documents justificatifs des prestations réalisées.',
              ]} />
            </Section>

            <Section id="obligations-pre" title="9. Obligations des prestataires">
              <P>Le prestataire s'engage à :</P>
              <Ul items={[
                'Exercer son activité dans un cadre légal (auto-entrepreneur, EURL, SASU, etc.) et à maintenir son statut en règle.',
                'Disposer des qualifications, diplômes ou certifications requis pour les prestations proposées.',
                'Fournir des informations exactes sur ses compétences, son expérience et sa localisation.',
                'Respecter les engagements pris lors de l\'acceptation d\'une mission (date, lieu, durée, qualité).',
                'Émettre les factures correspondant aux prestations réalisées.',
                'Disposer d\'une assurance responsabilité civile professionnelle couvrant les prestations réalisées via la plateforme.',
                'Respecter la confidentialité des informations transmises par les entreprises dans le cadre des missions.',
                'Ne pas sous-traiter les missions sans accord exprès de l\'entreprise cliente.',
                'Prévenir Velink et l\'entreprise dans les meilleurs délais en cas d\'empêchement ou d\'impossibilité de réaliser une mission acceptée.',
              ]} />
            </Section>

            <Section id="responsabilite" title="10. Responsabilité">
              <P>
                <strong className="text-text-primary">Rôle d'intermédiaire :</strong> Velink agit uniquement en qualité d'intermédiaire de mise en relation. Elle n'est pas partie au contrat de prestation conclu entre l'entreprise et le prestataire et ne peut être tenue responsable de la bonne exécution des prestations.
              </P>
              <P>
                <strong className="text-text-primary">Qualité des prestations :</strong> Velink ne garantit pas la qualité, la conformité ou les résultats des prestations réalisées par les prestataires. L'entreprise est seule juge de l'adéquation d'un prestataire à ses besoins.
              </P>
              <P>
                <strong className="text-text-primary">Disponibilité de la plateforme :</strong> Velink s'efforce d'assurer la disponibilité de la plateforme 24h/24 et 7j/7, mais ne saurait être tenue responsable des interruptions liées à des opérations de maintenance, des défaillances techniques ou des cas de force majeure.
              </P>
              <P>
                <strong className="text-text-primary">Limitation de responsabilité :</strong> en tout état de cause, la responsabilité de Velink est limitée au montant des commissions effectivement perçues au cours des 12 derniers mois précédant le fait dommageable. Velink ne saurait être tenue responsable des dommages indirects, pertes d'exploitation ou manques à gagner.
              </P>
              <P>
                <strong className="text-text-primary">Vérification des prestataires :</strong> Velink peut mettre en place des processus de vérification des prestataires (documents, certifications) mais ne garantit pas l'exhaustivité de ces vérifications.
              </P>
            </Section>

            <Section id="resiliation" title="11. Résiliation et suspension de compte">
              <P>
                <strong className="text-text-primary">Par l'utilisateur :</strong> tout utilisateur peut supprimer son compte à tout moment depuis les paramètres de son espace. La suppression prend effet sous 30 jours, délai permettant le règlement des missions en cours.
              </P>
              <P>
                <strong className="text-text-primary">Par Velink :</strong> Velink se réserve le droit de suspendre ou résilier tout compte, sans préavis ni indemnité, en cas de :
              </P>
              <Ul items={[
                'Violation des présentes CGV.',
                'Comportement frauduleux ou trompeur.',
                'Tentative de contournement de la plateforme.',
                'Fausses déclarations lors de l\'inscription.',
                'Non-respect répété des engagements envers les autres utilisateurs.',
                'Signalement fondé d\'un autre utilisateur.',
              ]} />
              <P>
                En cas de résiliation pour manquement, les fonds en séquestre pour des missions en cours seront traités au cas par cas après instruction.
              </P>
            </Section>

            <Section id="donnees" title="12. Données personnelles">
              <P>
                Velink collecte et traite des données personnelles conformément au Règlement Général sur la Protection des Données (RGPD — Règlement UE 2016/679) et à la loi Informatique et Libertés modifiée.
              </P>
              <P>
                <strong className="text-text-primary">Données collectées :</strong> identité, coordonnées, données de géolocalisation, données de paiement (traitées par Stripe/PayPal, non stockées par Velink), historique des missions, évaluations.
              </P>
              <P>
                <strong className="text-text-primary">Finalités :</strong> fourniture des services de mise en relation, traitement des paiements, amélioration de la plateforme, envoi de notifications liées aux missions, conformité réglementaire.
              </P>
              <P>
                <strong className="text-text-primary">Droits :</strong> vous disposez d'un droit d'accès, de rectification, d'effacement, de portabilité et d'opposition sur vos données. Pour exercer ces droits : {COMPANY_EMAIL}.
              </P>
              <P>
                Les données de géolocalisation sont utilisées uniquement pour le matching géographique mission/prestataire et ne sont jamais revendues à des tiers.
              </P>
            </Section>

            <Section id="pi" title="13. Propriété intellectuelle">
              <P>
                La plateforme Velink, son code source, son design, ses marques, logos et tout contenu produit par Velink sont la propriété exclusive de {COMPANY_NAME} et protégés par le droit de la propriété intellectuelle.
              </P>
              <P>
                L'utilisateur se voit concéder une licence d'utilisation personnelle, non exclusive, non cessible et révocable, limitée à l'usage de la plateforme conformément aux présentes CGV.
              </P>
              <P>
                Toute reproduction, représentation, modification ou exploitation non autorisée est strictement interdite et peut donner lieu à des poursuites judiciaires.
              </P>
              <P>
                Les contenus publiés par les utilisateurs (descriptions de missions, profils, évaluations) restent leur propriété. En les publiant sur la plateforme, ils concèdent à Velink une licence d'utilisation non exclusive pour les afficher dans le cadre du service.
              </P>
            </Section>

            <Section id="modifications" title="14. Modifications des CGV">
              <P>
                Velink se réserve le droit de modifier les présentes CGV à tout moment. Les modifications entrent en vigueur dès leur publication sur la plateforme. Les utilisateurs en sont informés par email et/ou notification in-app au moins <strong className="text-text-primary">30 jours avant</strong> l'entrée en vigueur des modifications substantielles.
              </P>
              <P>
                La poursuite de l'utilisation de la plateforme après notification vaut acceptation des nouvelles CGV. En cas de refus, l'utilisateur doit supprimer son compte avant la date d'entrée en vigueur.
              </P>
            </Section>

            <Section id="droit" title="15. Droit applicable et litiges">
              <P>
                Les présentes CGV sont soumises au droit français.
              </P>
              <P>
                <strong className="text-text-primary">Médiation :</strong> conformément aux articles L.616-1 et R.616-1 du Code de la consommation, Velink propose un dispositif de médiation de la consommation. En cas de litige non résolu amiablement, l'utilisateur peut recourir à un médiateur de la consommation.
              </P>
              <P>
                <strong className="text-text-primary">Règlement amiable :</strong> avant toute action judiciaire, les parties s'engagent à tenter de résoudre le litige à l'amiable. Toute réclamation doit être adressée à {COMPANY_EMAIL}. Velink s'engage à répondre sous 15 jours ouvrés.
              </P>
              <P>
                <strong className="text-text-primary">Juridiction compétente :</strong> à défaut de règlement amiable, tout litige sera soumis aux tribunaux compétents du ressort du siège social de {COMPANY_NAME}, sauf disposition légale impérative contraire.
              </P>
            </Section>

            {/* Informations légales */}
            <div className="glass-card p-6 rounded-2xl mt-8">
              <h2 className="text-sm font-semibold text-text-muted uppercase tracking-wider mb-4">Informations légales</h2>
              <div className="grid sm:grid-cols-2 gap-4 text-sm text-text-secondary">
                <div>
                  <p className="text-text-muted text-xs mb-1">Société éditrice</p>
                  <p className="text-text-primary font-medium">{COMPANY_NAME}</p>
                </div>
                <div>
                  <p className="text-text-muted text-xs mb-1">Adresse</p>
                  <p>{COMPANY_ADDRESS || '—'}</p>
                </div>
                <div>
                  <p className="text-text-muted text-xs mb-1">SIRET</p>
                  <p>{COMPANY_SIRET || '—'}</p>
                </div>
                <div>
                  <p className="text-text-muted text-xs mb-1">Contact</p>
                  <a href={`mailto:${COMPANY_EMAIL}`} className="text-accent hover:underline">{COMPANY_EMAIL}</a>
                </div>
              </div>
            </div>

            <p className="text-text-muted text-xs text-center pt-4">
              Version en vigueur depuis le {LAST_UPDATE} — <Link to="/mentions-legales" className="text-accent hover:underline">Mentions légales</Link>
            </p>

            </>)}

          </div>
        </div>
      </main>
    </div>
  )
}
